import React, { useCallback, useEffect } from 'react';
import { AppButton, Box, Preloader, Text, WarningIcon } from '@common-fe/common-fe';
import _ from 'lodash';

import { WarnModal } from '@/components/elements';

import { EmployeeCIPCloseModalButton } from './EmployeeCIPCloseModal';
import { useCIPInfo, useCIPInfoVerification } from './hooks';

interface WrapperProps {
  children: React.ReactNode;
  isStandaloneMode?: boolean;
}
const Wrapper: React.FC<WrapperProps> = ({ isStandaloneMode, children }) => {
  if (isStandaloneMode) {
    return <Box margin={{ bottom: 'l' }}>{children}</Box>;
  }
  return (
    <Box
      elevation="default"
      data-testid="employee-cip-verification-banner"
      round="container1Round"
      background="canvas"
      pad={{ vertical: 'l', horizontal: 'xl' }}
      margin={{ bottom: 'l' }}
    >
      {children}
    </Box>
  );
};
interface Props {
  employeeId: string;
  accountId?: string;
  isStandaloneMode?: boolean;
}
const STORAGE_KEY = 'CLOSE_CIP:HIDDEN-';
const EmployeeCIPVerificationBanner: React.FC<Props> = ({ employeeId, isStandaloneMode, accountId }) => {
  const { isVerificationRequired, handleVerify, isVerificationLoading, isShowErrorModal, setShowErrorModal } =
    useCIPInfo({ employeeId });

  const { failedEnrollment, closedEnrollmentIds } = useCIPInfoVerification({ employeeId, accountId });

  const isClosureIsInProgress = useCallback(
    (planId: string) => {
      const currentKey = `${STORAGE_KEY}${employeeId}/${planId}`;
      return !!localStorage.getItem(currentKey);
    },
    [employeeId]
  );

  const currentBannerText = useCallback(
    (planId: string) => {
      return isClosureIsInProgress(planId)
        ? ' Closure in progress'
        : ' - We cannot verify your details with the information provided';
    },
    [isClosureIsInProgress]
  );

  useEffect(() => {
    if (closedEnrollmentIds?.length > 0) {
      for (const id of closedEnrollmentIds) {
        const currentKey = `${STORAGE_KEY}${employeeId}/${id}`;
        if (localStorage.getItem(currentKey)) {
          localStorage.removeItem(currentKey);
        }
      }
    }
  }, [closedEnrollmentIds, employeeId]);
  if (!isVerificationRequired) {
    return null;
  }

  return (
    <div>
      <WarnModal
        visible={isShowErrorModal}
        testId="CIP-banner_server_error"
        onSetVisible={setShowErrorModal}
        header="The server encountered an error processing the request"
        helptext={`It doesn't appear to have affected your data, but we cannot verify your identity to establish your HSA.
        Our technical staff have been automatically notified and will be looking into this with the utmost urgency.`}
        buttonText="Close"
        onSubmit={handleVerify}
        submitButtonText="Try Again"
      />

      {!!failedEnrollment.length && (
        <Wrapper isStandaloneMode={isStandaloneMode}>
          {failedEnrollment.map((item) => (
            <Box
              key={item.id}
              margin={{ bottom: _.last(failedEnrollment) === item ? '' : 'spacing16' }}
              pad={{ vertical: 'l', horizontal: 'xl' }}
              data-testid="cip-banner"
              round="container2Round"
              direction="column"
              justify="center"
              background="warningContainer"
              border={{ color: 'warningBorder' }}
              style={{ position: 'relative' }}
            >
              <Box
                height="2px"
                background="warning"
                style={{ position: 'absolute', top: 0, left: '32px', right: '32px' }}
              />
              <Box direction="row" align="center">
                <Box fill>
                  <Box direction="row" round="container1Round" align="center">
                    <WarningIcon size="24px" color="danger" />
                    <Text color="textBody" margin={{ left: 'spacing8' }} size="large" weight="bold">
                      {item.name}
                      {currentBannerText(item.id)}
                    </Text>
                  </Box>

                  {/* <Text color="textBody" size="medium"> */}
                  {/*   With just a few extra details we can activate your account. */}
                  {/* </Text> */}
                </Box>
                <div>
                  <Box direction="row" align="center">
                    <EmployeeCIPCloseModalButton
                      employeeId={employeeId}
                      disabled={isVerificationLoading}
                      planId={item.id}
                      planName={item.name}
                    />
                    {isClosureIsInProgress(item.id) ? null : (
                      <Box margin={{ left: 'spacing24' }}>
                        <AppButton
                          testId="cip-banner_verify"
                          onClick={handleVerify}
                          disabled={isVerificationLoading}
                          width="180px"
                        >
                          {isVerificationLoading ? <Preloader /> : 'Verify Identity'}
                        </AppButton>
                      </Box>
                    )}
                  </Box>
                </div>
              </Box>
            </Box>
          ))}
        </Wrapper>
      )}
    </div>
  );
};

export default EmployeeCIPVerificationBanner;
